import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {Users, Trophy, Info} from 'lucide-react';
import { Link } from "react-router-dom";

type ScrambleFormat = "2-man" | "4-man";

function ScrambleTools() {
  const [format, setFormat] = useState<ScrambleFormat>("4-man");
  const [handicaps, setHandicaps] = useState<string[]>(["", "", "", ""]);
  const [teamHandicap, setTeamHandicap] = useState<number | null>(null);

  const updateHandicap = (index: number, value: string) => {
    const newHandicaps = [...handicaps];
    newHandicaps[index] = value;
    setHandicaps(newHandicaps);
    setTeamHandicap(null);
  };

  const calculateTeamHandicap = () => {
    const validHandicaps = handicaps
      .slice(0, format === "2-man" ? 2 : 4)
      .map(h => parseFloat(h))
      .filter(h => !isNaN(h))
      .sort((a, b) => a - b);

    if (validHandicaps.length === (format === "2-man" ? 2 : 4)) {
      let result = 0;
      if (format === "2-man") {
        // 2-man: 35% of lower + 15% of higher
        result = validHandicaps[0] * 0.35 + validHandicaps[1] * 0.15;
      } else {
        // 4-man: 20% A + 15% B + 10% C + 5% D
        result = validHandicaps[0] * 0.20 + validHandicaps[1] * 0.15 + 
                 validHandicaps[2] * 0.10 + validHandicaps[3] * 0.05;
      }
      setTeamHandicap(Math.round(result * 10) / 10);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Scramble & Tournament Handicap Tools
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Calculate team handicaps for scramble golf tournaments. Perfect for tournament organizers and players preparing for team events.
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#006747] rounded-lg flex items-center justify-center">
                <Users className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Scramble Handicap Calculator
              </h2>
            </div>

            {/* Format Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Scramble Format
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setFormat("2-man");
                    setTeamHandicap(null);
                  }}
                  className={`py-3 px-4 rounded-lg font-semibold transition-colors ${
                    format === "2-man"
                      ? "bg-[#006747] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  2-Man Scramble
                </button>
                <button
                  onClick={() => {
                    setFormat("4-man");
                    setTeamHandicap(null);
                  }}
                  className={`py-3 px-4 rounded-lg font-semibold transition-colors ${
                    format === "4-man"
                      ? "bg-[#006747] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  4-Man Scramble
                </button>
              </div>
            </div>

            {/* Handicap Inputs */}
            <div className="space-y-4 mb-6">
              {(format === "2-man" ? [0, 1] : [0, 1, 2, 3]).map((index) => (
                <div key={index}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Player {String.fromCharCode(65 + index)} Handicap
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={handicaps[index]}
                    onChange={(e) => updateHandicap(index, e.target.value)}
                    placeholder={`e.g., ${5 + index * 3}.0`}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                  />
                </div>
              ))}
            </div>

            <button
              onClick={calculateTeamHandicap}
              className="w-full bg-[#006747] text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#005239] transition-colors shadow-lg"
            >
              Calculate Team Handicap
            </button>

            {teamHandicap !== null ? (
              <div className="mt-6 bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <Trophy className="text-[#006747]" size={28} />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Team Handicap:
                  </h3>
                </div>
                <p className="text-4xl font-bold text-[#006747] mb-3">
                  {teamHandicap}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  {format === "2-man"
                    ? "Calculated using: 35% of lower handicap + 15% of higher handicap"
                    : "Calculated using: 20% A + 15% B + 10% C + 5% D (sorted low to high)"}
                </p>
                <Link
                  to="/formulas"
                  className="inline-flex items-center gap-2 text-[#006747] font-medium mt-2 hover:underline"
                >
                  <Info size={16} />
                  Learn more about scramble formats
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Understanding Scramble Golf Formats
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#006747]">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What is a Golf Scramble?
              </h3>
              <p className="text-gray-700 mb-4">
                A scramble is a popular team golf format where all players tee off, then the team selects the best shot. All players then play their next shot from that location. This continues until the ball is holed.
              </p>
              <p className="text-gray-700">
                Team handicaps are calculated using percentages of individual handicaps to maintain fairness while accounting for the team advantage.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-[#006747] mb-3">
                  2-Man Scramble Formula
                </h4>
                <div className="bg-gray-50 p-4 rounded mb-3">
                  <code className="text-sm font-mono text-gray-800">
                    Team = (35% × Low) + (15% × High)
                  </code>
                </div>
                <p className="text-sm text-gray-600">
                  Total: 50% of combined handicaps
                </p>
                <div className="mt-4 space-y-1 text-sm text-gray-600">
                  <p><strong>Example:</strong></p>
                  <p>Player A: 8.0 handicap</p>
                  <p>Player B: 14.0 handicap</p>
                  <p className="text-[#006747] font-semibold">Team: (8.0 × 0.35) + (14.0 × 0.15) = 4.9</p>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-[#006747] mb-3">
                  4-Man Scramble Formula
                </h4>
                <div className="bg-gray-50 p-4 rounded mb-3">
                  <code className="text-sm font-mono text-gray-800">
                    Team = 20%A + 15%B + 10%C + 5%D
                  </code>
                </div>
                <p className="text-sm text-gray-600">
                  Total: 50% of combined handicaps (sorted low to high)
                </p>
                <div className="mt-4 space-y-1 text-sm text-gray-600">
                  <p><strong>Example:</strong></p>
                  <p>Players: 6, 10, 14, 18 handicaps</p>
                  <p className="text-[#006747] font-semibold">Team: (6×0.2) + (10×0.15) + (14×0.1) + (18×0.05) = 5.4</p>
                </div>
              </div>
            </div>

            <div className="bg-[#F0FDF4] rounded-lg p-6 border border-[#22C55E]">
              <h4 className="font-semibold text-gray-900 mb-3">
                🏆 Tournament Organizer Tips
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Verify handicaps:</strong> Request GHIN cards or official handicap certificates</li>
                <li>• <strong>Set maximums:</strong> Consider capping individual handicaps (e.g., max 20)</li>
                <li>• <strong>Course handicaps:</strong> Always use course handicaps, not handicap indexes</li>
                <li>• <strong>Format variations:</strong> Some tournaments use different percentages - clearly communicate your formula</li>
              </ul>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Shamble Format (Variation)
              </h4>
              <p className="text-gray-700 mb-3">
                A <strong>shamble</strong> is similar to a scramble, but only the tee shot is selected as a team. After that, each player plays their own ball from the selected location.
              </p>
              <p className="text-gray-600 text-sm">
                Handicap calculation: Typically uses 25% of each player's course handicap (for 4-man) or full handicaps applied individually.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/usga-calculator"
              className="flex-1 bg-[#006747] text-white text-center py-3 px-6 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
            >
              Calculate Course Handicap
            </Link>
            <Link
              to="/formulas"
              className="flex-1 bg-white text-[#006747] text-center py-3 px-6 rounded-lg font-semibold border-2 border-[#006747] hover:bg-gray-50 transition-colors"
            >
              View All Formulas
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ScrambleTools;
